import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { getPartnerDetail } from "@/api/partners/partnersApi";
import {
  getPartnerDetailQueryKey,
  queryClient,
  removePersistedQueries
} from "@/lib/react-query";
import { initTabStorage } from "@/store/tabStore";
import { getBasePath, withBasePath } from "@/utils/basePath";
import { isTracedApiRequest, maskRumEventUrls } from "@/utils/datadog";
import { getSubdomain } from "@/utils/location";

import { datadogRum } from "@datadog/browser-rum";

import App from "./App.tsx";

import { baseUrl } from "./api/config.ts";
import { EXCLUDED_EMAILS_FROM_TRACKING } from "./constants/user.ts";
import { shouldSkipPartnerCheck } from "./utils/partner.ts";

import "maplibre-gl/dist/maplibre-gl.css";
import "./index.css";

/**
 * Log to Datadog only in pre-production and production, and skip headless
 * browsers so automation runs don't pollute the data.
 */
const runDatadogAnalytics =
  import.meta.env.VITE_ENABLE_DATA_DOG_ANALYTICS === "true" &&
  !/headless/i.test(navigator.userAgent) &&
  (import.meta.env.MODE === "preproduction" ||
    import.meta.env.MODE === "production");

if (runDatadogAnalytics) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATA_DOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN,
    site: import.meta.env.VITE_DATA_DOG_SITE,
    service: import.meta.env.VITE_APP_NAME,
    version: import.meta.env.VITE_APP_VERSION,
    env: import.meta.env.MODE,
    sessionSampleRate: Number(import.meta.env.VITE_SESSION_SAMPLE_RATE ?? 0),
    sessionReplaySampleRate: Number(
      import.meta.env.VITE_SESSION_REPLAY_SAMPLE_RATE ?? 0
    ),
    allowedTracingUrls: [
      {
        match: (url: string) =>
          isTracedApiRequest(url, baseUrl, window.location.origin),
        propagatorTypes: ["tracecontext", "datadog"]
      }
    ],
    traceSampleRate: Number(import.meta.env.VITE_TRACE_SAMPLE_RATE ?? 0),
    defaultPrivacyLevel: "mask-user-input",
    // Returning false discards the event; it never gets sent.
    beforeSend: (event) => {
      // `usr.id` holds the user's email, so match it against the blacklist.
      if (EXCLUDED_EMAILS_FROM_TRACKING.has(event?.usr?.id || "")) return false;

      // `mask-user-input` covers input values, not URLs — strip the coordinates
      // a place search carries before the event leaves the browser.
      maskRumEventUrls(event);

      return true;
    }
  });
}

async function enableMocking() {
  if (import.meta.env.VITE_API_MOCKING === "enabled") {
    const { worker } = await import("./mocks/browser.ts");

    const basePath = getBasePath();
    // Slash-less scope so the worker also controls "/intelligence" (no slash).
    const scope = basePath.slice(0, -1) || "/";

    return worker.start({
      serviceWorker: {
        url: withBasePath("mockServiceWorker.js"),
        options: { scope }
      },
      onUnhandledRequest: "bypass"
    });
  }
}

// TODO: re-enable service worker registration once the config is ready.

const unregisterServiceWorker = () => {
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    const normalizedBasePath = getBasePath();

    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        if (
          registration.scope.includes(
            `${window.location.origin}${normalizedBasePath}`
          )
        ) {
          console.log(
            `Unregistering service worker with scope: ${registration.scope}`
          );
          registration.unregister();
        }
      });
    });
  }
};

unregisterServiceWorker();

enableMocking().then(async () => {
  // Run the storage migration + open the readiness gate BEFORE render, so
  // redux-persist rehydrates from the correct (namespaced) per-tab keys.
  await initTabStorage();

  const appSubdomain = getSubdomain();

  if (shouldSkipPartnerCheck(appSubdomain)) {
    // Partner detail isn't fetched on non-partner hosts, so a persisted entry
    // would never be refreshed and could keep showing stale branding. Prune
    // only that one — countries and admin-area hierarchies must survive.
    await removePersistedQueries(
      (queryKey) => queryKey[0] === "getPartnerDetail"
    );
  } else {
    try {
      await queryClient.fetchQuery({
        queryKey: getPartnerDetailQueryKey(appSubdomain),
        queryFn: () => getPartnerDetail(appSubdomain),
        meta: { shouldPersist: true },
        staleTime: 24 * 60 * 60 * 1000
      });
    } catch {
      queryClient.removeQueries({
        queryKey: getPartnerDetailQueryKey(appSubdomain)
      });
      window.location.href = import.meta.env.VITE_APP_URL;
      return;
    }
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
});
